import { QueryClient } from '@tanstack/react-query'
import {
  MARKETING_FUNNELS_KEY,
  postMarketingFunnel,
  postMarketingFunnelPeriodBulk,
  ORGANIC_FUNNELS_KEY,
  postOrganicFunnel,
  postOrganicFunnelPeriodBulk,
  MARKETING_ORDERS_ALLOCATION_KEY,
  postMarketingOrdersAllocationBulk,
  ORGANIC_ORDERS_ALLOCATION_KEY,
  postOrganicOrdersAllocationBulk,
  ORGANIC_DIRECT_RESIDUAL_ORDERS_KEY,
  postOrganicDirectResidualOrdersBulk,
  SALES_FUNNELS_KEY,
  postSalesFunnel,
  postSalesFunnelPeriodBulk,
  SALES_ORDERS_ALLOCATION_KEY,
  postSalesOrdersAllocationBulk
} from '@/feature/financial-model/ecommerce/revenues/api'
import { resetGlobalQueryKey, TOTALS_KEY } from '@/api/query-keys'
import { parsePeriod, filterRowsStartingFrom } from '@/utils/sidepanel'

const applyFunnelValueBulkGeneric = async <TFunnel extends { data: any[]; funnel_id?: string }>({
  payload,
  value,
  funnel,
  funnelType,
  channelId,
  subChannelId,
  startFrom,
  exactPeriod,
  financialModelId,
  queryClient,
  postFunnelFn,
  postBulkFn,
  queryKey,
  mapRowToBody
}: {
  payload: any
  value: number
  funnel: TFunnel
  funnelType: 'marketing' | 'organic' | 'sales'
  channelId: string
  subChannelId: string | null
  startFrom?: { year: number; month: number }
  exactPeriod?: { year: number; month: number }
  financialModelId: string
  queryClient: QueryClient
  postFunnelFn: (params: any) => Promise<{ funnel_id: string }>
  postBulkFn: (fmId: string, charId: string, subId: string | null, funId: string, bulkData: any[]) => Promise<any>
  queryKey: any[]
  mapRowToBody: (row: any, rowValue: number, mutateDataKey: string) => any
}) => {
  const mutateDataKey = payload?.mutateDataKey as string
  if (!mutateDataKey || !funnel?.data?.length) return
  const periodValuesByKey = payload?.periodValuesByKey as Record<string, number> | undefined

  const periodType = (payload as any)?.queryPeriod ?? `forecast`
  const forecastRows = funnel.data.filter((row: any) => (row.type ?? row.period_type) === periodType)

  if (!forecastRows.length) return

  const rowsToUpdate = periodValuesByKey
    ? forecastRows.filter((row: any) => getMappedValueForRow(row, periodValuesByKey) !== undefined)
    : filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

  if (!rowsToUpdate.length) return

  let funnelId = funnel.funnel_id

  if (!funnelId) {
    const funnelParams: any = { financial_model_id: financialModelId, sub_channel_id: subChannelId }
    if (funnelType === 'marketing') funnelParams.marketing_channel_id = channelId
    else if (funnelType === 'organic') funnelParams.organic_channel_id = channelId
    else funnelParams.sales_channel_id = channelId

    const res = await postFunnelFn(funnelParams)
    funnelId = res.funnel_id
  }

  const bulkPayload = rowsToUpdate.map((row: any) => {
    const { year, month } = parsePeriod(row.period)
    const rowValue = getMappedValueForRow(row, periodValuesByKey) ?? value

    return {
      ...mapRowToBody(row, rowValue, mutateDataKey),
      year: year ?? 0,
      month: month ?? 0,
      type: (row as any).type ?? (row as any).period_type,
      config: row.config
    }
  })

  await postBulkFn(financialModelId, channelId, subChannelId, funnelId, bulkPayload)

  queryClient.invalidateQueries({ queryKey })
  resetGlobalQueryKey([TOTALS_KEY])
}

type FunnelParams = {
  payload: any
  value: number
  periodValuesByKey?: Record<string, number>
  startFrom?: { year: number; month: number }
  exactPeriod?: { year: number; month: number }
  financialModelId: string
  queryClient: QueryClient
}

const getPeriodMapKey = (year: number, month: number) => `${year}-${String(month).padStart(2, `0`)}`

const getMappedValueForRow = (row: any, periodValuesByKey?: Record<string, number>) => {
  if (!periodValuesByKey) return undefined

  const { year, month } = parsePeriod(row.period)

  if (year === undefined || month === undefined) return undefined

  return periodValuesByKey[getPeriodMapKey(year, month)]
}

export const applyMarketingFunnelValue = async (params: FunnelParams) => {
  const funnel = params.payload.marketingFunnel
  if (!funnel) return

  return applyFunnelValueBulkGeneric({
    ...params,
    funnel,
    funnelType: 'marketing',
    channelId: funnel.marketing_channel_id,
    subChannelId: funnel.sub_channel_id,
    postFunnelFn: postMarketingFunnel,
    postBulkFn: postMarketingFunnelPeriodBulk,
    queryKey: MARKETING_FUNNELS_KEY,
    mapRowToBody: (row, rowValue, key) => ({
      expense: row.expense,
      impressions: row.impressions,
      sessions: row.sessions,
      conversion_rate: row.conversion_rate,
      orders: row.orders,
      cpm: row.cpm,
      cost_per_session: row.cost_per_session,
      cost_per_order: row.cost_per_order,
      leads: row.leads,
      lead_conversion_rate: row.lead_conversion_rate,
      participants: row.participants,
      media_outlets: row.media_outlets,
      avg_impressions_media_outlets: row.avg_impressions_media_outlets,
      number_of_posts: row.number_of_posts,
      avg_impressions_per_post: row.avg_impressions_per_post,
      click_through_rate: row.click_through_rate,
      cost_per_participant: row.cost_per_participant,
      cost_per_lead: row.cost_per_lead,
      expense_absolute: row.expense_absolute,
      [key]: rowValue
    })
  })
}

export const applyOrganicFunnelValue = async (params: FunnelParams) => {
  const funnel = params.payload.organicFunnel
  if (!funnel) return

  return applyFunnelValueBulkGeneric({
    ...params,
    funnel,
    funnelType: 'organic',
    channelId: funnel.organic_channel_id,
    subChannelId: funnel.sub_channel_id,
    postFunnelFn: postOrganicFunnel,
    postBulkFn: postOrganicFunnelPeriodBulk,
    queryKey: ORGANIC_FUNNELS_KEY,
    mapRowToBody: (row, rowValue, key) => ({
      orders: row.orders,
      impressions: row.impressions,
      sessions: row.sessions,
      posts: row.posts,
      avg_impressions_per_post: row.avg_impressions_per_post,
      market_share_of_search_volume: row.market_share_of_search_volume,
      search_volumes: row.search_volumes,
      keywords: row.keywords,
      avg_search_volume_per_keyword: row.avg_search_volume_per_keyword,
      newsletters_sent: row.newsletters_sent,
      emails_sent: row.emails_sent,
      email_addresses: row.email_addresses,
      conversion_rate: row.conversion_rate,
      click_through_rate: row.click_through_rate,
      [key]: rowValue
    })
  })
}

export const applySalesFunnelValue = async (params: FunnelParams) => {
  const funnel = params.payload.salesFunnel
  if (!funnel) return

  return applyFunnelValueBulkGeneric({
    ...params,
    funnel,
    funnelType: 'sales',
    channelId: funnel.sales_channel_id,
    subChannelId: (funnel as any).sub_channel_id,
    postFunnelFn: postSalesFunnel,
    postBulkFn: postSalesFunnelPeriodBulk,
    queryKey: SALES_FUNNELS_KEY,
    mapRowToBody: (row, rowValue, key) => {
      const baseBody: any = {
        expense_categories: row.expense_categories,
        stores: row.stores,
        sqm: row.sqm,
        commission_to_third_party: row.commission_to_third_party,
        commission_to_personnel: row.commission_to_personnel,
        fte: row.fte,
        orders: row.orders,
        avg_foot_traffic_per_store: row.avg_foot_traffic_per_store,
        foot_traffic: row.foot_traffic,
        leads: row.leads,
        quotes: row.quotes,
        conversion_rate_leads_to_quotes: row.conversion_rate_leads_to_quotes,
        conversion_rate_quotes_to_orders: row.conversion_rate_quotes_to_orders,
        conversion_rate: row.conversion_rate,
        conversion_rate_leads_to_orders: row.conversion_rate_leads_to_orders,
        cost_per_order: row.cost_per_order,
        expense: row.expense
      }

      const expenseCategoryId = (params.payload as any)?.expense_category_id

      if (key === `expense` && expenseCategoryId) {
        baseBody.expense_categories = (row.expense_categories || []).map((c: any) =>
          c.category_id === expenseCategoryId ? { ...c, expense: rowValue } : c
        )
      } else {
        baseBody[key] = rowValue
      }

      return baseBody
    }
  })
}

export const applyMarketingOrderAllocationValue = async (params: FunnelParams) => {
  const { payload, value, startFrom, exactPeriod, queryClient } = params
  const periodValuesByKey = params.periodValuesByKey ?? (payload?.periodValuesByKey as Record<string, number> | undefined)
  const marketingOrderAllocationPeriods = payload?.marketingOrderAllocationPeriods
  const funnelId = payload?.funnelId
  const orderTypeId = payload?.orderTypeId
  const customerSegmentId = payload?.customerSegmentId

  if (!marketingOrderAllocationPeriods?.length || !funnelId || !orderTypeId || !customerSegmentId) return

  const forecastRows = marketingOrderAllocationPeriods.filter((period: any) => period.type === `forecast`)

  if (!forecastRows.length) return

  const rowsToUpdate = periodValuesByKey
    ? forecastRows.filter((period: any) => getMappedValueForRow(period, periodValuesByKey) !== undefined)
    : filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

  if (!rowsToUpdate.length) return

  const bulkData = rowsToUpdate.map((period: any) => ({
    year: period.fiscal_year,
    month: period.fiscal_month,
    type: period.type,
    customer_segment_id: customerSegmentId,
    order_type_id: orderTypeId,
    order_percentage: String(getMappedValueForRow(period, periodValuesByKey) ?? value)
  }))

  await postMarketingOrdersAllocationBulk(funnelId, bulkData)

  queryClient.invalidateQueries({
    queryKey: MARKETING_ORDERS_ALLOCATION_KEY
  })
  resetGlobalQueryKey([TOTALS_KEY])
}

export const applyOrganicOrderAllocationValue = async (params: FunnelParams) => {
  const { payload, value, startFrom, exactPeriod, queryClient } = params
  const periodValuesByKey = params.periodValuesByKey ?? (payload?.periodValuesByKey as Record<string, number> | undefined)
  const organicOrdersAllocationPeriods = payload?.organicOrdersAllocationPeriods
  const funnelId = payload?.funnelId
  const orderTypeId = payload?.orderTypeId
  const customerSegmentId = payload?.customerSegmentId

  if (!organicOrdersAllocationPeriods?.length || !funnelId || !orderTypeId || !customerSegmentId) return

  const forecastRows = organicOrdersAllocationPeriods.filter((period: any) => period.type === `forecast`)

  if (!forecastRows.length) return

  const rowsToUpdate = periodValuesByKey
    ? forecastRows.filter((period: any) => getMappedValueForRow(period, periodValuesByKey) !== undefined)
    : filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

  if (!rowsToUpdate.length) return

  const bulkData = rowsToUpdate.map((period: any) => ({
    year: period.fiscal_year,
    month: period.fiscal_month,
    type: period.type,
    customer_segment_id: customerSegmentId,
    order_type_id: orderTypeId,
    order_percentage: String(getMappedValueForRow(period, periodValuesByKey) ?? value)
  }))

  await postOrganicOrdersAllocationBulk(funnelId, bulkData)

  queryClient.invalidateQueries({
    queryKey: ORGANIC_ORDERS_ALLOCATION_KEY
  })
  resetGlobalQueryKey([TOTALS_KEY])
}

export const applySalesOrderAllocationValue = async (params: FunnelParams) => {
  const { payload, value, startFrom, exactPeriod, queryClient } = params
  const periodValuesByKey = params.periodValuesByKey ?? (payload?.periodValuesByKey as Record<string, number> | undefined)
  const salesOrdersAllocationPeriods = payload?.salesOrdersAllocationPeriods
  const funnelId = payload?.funnelId
  const orderTypeId = payload?.orderTypeId
  const customerSegmentId = payload?.customerSegmentId

  if (!salesOrdersAllocationPeriods?.length || !funnelId || !orderTypeId || !customerSegmentId) return

  const forecastRows = salesOrdersAllocationPeriods.filter((period: any) => period.type === `forecast`)

  if (!forecastRows.length) return

  const rowsToUpdate = periodValuesByKey
    ? forecastRows.filter((period: any) => getMappedValueForRow(period, periodValuesByKey) !== undefined)
    : filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

  if (!rowsToUpdate.length) return

  const bulkData = rowsToUpdate.map((period: any) => ({
    year: period.fiscal_year,
    month: period.fiscal_month,
    type: period.type,
    customer_segment_id: customerSegmentId,
    order_type_id: orderTypeId,
    order_percentage: String(getMappedValueForRow(period, periodValuesByKey) ?? value)
  }))

  await postSalesOrdersAllocationBulk(funnelId, bulkData)

  queryClient.invalidateQueries({
    queryKey: SALES_ORDERS_ALLOCATION_KEY
  })
  resetGlobalQueryKey([TOTALS_KEY])
}

export const applyOrganicDirectResidualOrdersValue = async (params: FunnelParams) => {
  const { payload, value, startFrom, exactPeriod, financialModelId, queryClient } = params
  const periodValuesByKey = params.periodValuesByKey ?? (payload?.periodValuesByKey as Record<string, number> | undefined)
  const directResidualPeriods = payload?.organicDirectResidualOrdersPeriods
  const orderTypeId = payload?.orderTypeId
  const customerSegmentId = payload?.customerSegmentId
  const mutateDataKey = payload?.mutateDataKey

  if (!mutateDataKey || !directResidualPeriods?.length || !orderTypeId || !customerSegmentId) return

  const periodType = payload?.queryPeriod ?? `forecast`
  const forecastRows = directResidualPeriods.filter((period: any) => (period.period_type || period.type) === periodType)

  if (!forecastRows.length) return

  const rowsToUpdate = periodValuesByKey
    ? forecastRows.filter((period: any) => getMappedValueForRow(period, periodValuesByKey) !== undefined)
    : filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

  if (!rowsToUpdate.length) return

  const bulkData = rowsToUpdate.map((period: any) => {
    const mappedValue = getMappedValueForRow(period, periodValuesByKey)
    const valueToApply = mappedValue ?? value

    const body: any = {
      year: period.fiscal_year,
      month: period.fiscal_month,
      type: (period as any).type ?? (period as any).period_type,
      financial_model_id: financialModelId,
      customer_segment_id: customerSegmentId,
      order_type_id: orderTypeId,
      total_orders: Number((period as any).total_orders ?? 0),
      order_percentage: (period as any).order_percentage !== null ? String((period as any).order_percentage) : null,
      implied_orders: (period as any).implied_orders !== null ? Number((period as any).implied_orders) : null
    }

    if (mutateDataKey === 'order_percentage') {
      body[mutateDataKey] = String(valueToApply)
    } else {
      body[mutateDataKey] = Math.round(valueToApply)
    }
    return body
  })

  await postOrganicDirectResidualOrdersBulk(bulkData)

  queryClient.invalidateQueries({ queryKey: ORGANIC_DIRECT_RESIDUAL_ORDERS_KEY })

  try {
    const fullKey = ORGANIC_DIRECT_RESIDUAL_ORDERS_KEY.concat([financialModelId, payload?.queryPeriod ?? `forecast`, payload?.view ?? `monthly`])
    queryClient.invalidateQueries({ queryKey: fullKey })
  } catch (error) {
    console.error(error)
  }

  resetGlobalQueryKey([TOTALS_KEY])
}

export const applyEcommerceChannelsValues = async (params: FunnelParams) => {
  const { payload } = params

  if (payload.mutateDataKey === 'order_percentage') {
    if (payload.marketingOrderAllocationPeriods) {
      return applyMarketingOrderAllocationValue(params)
    }

    if (payload.organicOrdersAllocationPeriods) {
      return applyOrganicOrderAllocationValue(params)
    }

    if (payload.salesOrdersAllocationPeriods) {
      return applySalesOrderAllocationValue(params)
    }
  }

  if (payload.organicDirectResidualOrdersPeriods) {
    return applyOrganicDirectResidualOrdersValue(params)
  }

  if (payload.marketingFunnel) return applyMarketingFunnelValue(params)
  if (payload.organicFunnel) return applyOrganicFunnelValue(params)
  if (payload.salesFunnel) return applySalesFunnelValue(params)
}
